import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Transaction, TransactionType, PaymentMethod } from '../types'
import { getInvoice, updateInvoice } from './invoices'
import { localDateString } from '../utils/formatters'

export type TransactionPayload = {
  type: TransactionType // Required
  amount: number // Required
  transaction_date: string // Required
  description?: string | null
  reference_number?: string | null
  payment_method?: PaymentMethod | null
  client_id?: string | null
  deal_id?: string | null
  invoice_id?: string | null
  contract_id?: string | null
  notes?: string | null
  category?: string | null
  agency_id?: string
  created_by?: string | null
}

export type TransactionFilters = {
  type?: TransactionType
  client_id?: string
  deal_id?: string
  invoice_id?: string
  contract_id?: string
  payment_method?: PaymentMethod
  start_date?: string
  end_date?: string
  // Legacy filter aliases for backward compatibility
  related_to?: string
  related_id?: string
}


export type PaginatedTransactionsResult = {
  items: Transaction[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

function mapTransaction(record: Record<string, any>): Transaction {
  return {
    id: record.id,
    type: record.type ?? 'payment_received',
    amount: record.amount,
    transaction_date: record.transaction_date ?? '',
    description: record.description ?? null,
    reference_number: record.reference_number ?? null,
    payment_method: record.payment_method ?? null,
    client_id: record.client_id ?? null,
    deal_id: record.deal_id ?? null,
    invoice_id: record.invoice_id ?? null,
    contract_id: record.contract_id ?? null,
    notes: record.notes ?? null,
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
    // Legacy aliases for backward compatibility with existing codebase
    owner_id: record.created_by ?? record.owner_id ?? '',
    category: record.category ?? record.description ?? null,
    // A row with no status is not known to have cleared; it says so rather than pretending.
    status: record.status || null,
    player_id: record.player_id ?? record.client_id ?? null,
    related_to: record.related_to ?? null,
    related_id: record.related_id ?? null,
  }
}

export async function listTransactions(filters?: TransactionFilters) {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  // If no agency, return empty - user needs to complete onboarding
  if (!agencyId) {
    return []
  }

  const filterParts: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

  if (filters?.type) {
    filterParts.push(`type = "${escapeFilterValue(filters.type)}"`)
  }
  if (filters?.client_id) {
    filterParts.push(`client_id = "${escapeFilterValue(filters.client_id)}"`)
  }
  if (filters?.deal_id) {
    filterParts.push(`deal_id = "${escapeFilterValue(filters.deal_id)}"`)
  }
  if (filters?.invoice_id) {
    filterParts.push(`invoice_id = "${escapeFilterValue(filters.invoice_id)}"`)
  }
  if (filters?.contract_id) {
    filterParts.push(`contract_id = "${escapeFilterValue(filters.contract_id)}"`)
  }
  if (filters?.payment_method) {
    filterParts.push(`payment_method = "${escapeFilterValue(filters.payment_method)}"`)
  }
  if (filters?.start_date) {
    filterParts.push(`transaction_date >= "${escapeFilterValue(filters.start_date)}"`)
  }
  if (filters?.end_date) {
    filterParts.push(`transaction_date <= "${escapeFilterValue(filters.end_date)}"`)
  }
  // Support legacy related_to/related_id pattern (maps to client_id)
  if (filters?.related_to === 'client' && filters?.related_id && !filters?.client_id) {
    filterParts.push(`client_id = "${escapeFilterValue(filters.related_id)}"`)
  }

  const filter = filterParts.join(' && ')
  const data = await pb.collection('transactions').getFullList<Record<string, any>>({
    filter,
    sort: '-transaction_date',
  })
  return data.map(mapTransaction)
}



export async function listTransactionsPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedTransactionsResult> {
  const agencyId = (pb.authStore.model as any)?.agency_id
  if (!agencyId) {
    return { items: [], page, perPage, totalItems: 0, totalPages: 0 }
  }

  const agencyFilter = `agency_id = "${escapeFilterValue(agencyId)}"`
  const combinedFilter = options?.filter
    ? `${agencyFilter} && (${options.filter})`
    : agencyFilter

  const result = await pb.collection('transactions').getList<Record<string, any>>(page, perPage, {
    sort: options?.sort ?? '-transaction_date',
    filter: combinedFilter,
  })
  return {
    items: result.items.map(mapTransaction),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

export async function getTransaction(transactionId: string) {
  try {
    const record = await pb.collection('transactions').getOne<Record<string, any>>(transactionId)
    return mapTransaction(record)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      console.warn('Transaction not found or inaccessible', error)
      throw new Error('Transaction not found')
    }
    throw error
  }
}

/** Transaction types that pay an invoice down. Refunds and expenses do not. */
const INVOICE_PAYMENT_TYPES = new Set<string>(['payment_received', 'commission_payment', 'income'])

/**
 * Recompute an invoice's paid amount, balance and status from its payments.
 *
 * Until Sep 2026 this ran only on create, summed every linked transaction
 * (so a refund counted as a payment), never wrote amount_paid or
 * balance_due (so the Revenue page reported nothing outstanding), and
 * demoted a sent invoice to draft when its payments were removed.
 *
 * Runs after create, update and delete. Throws on failure: a payment that
 * saved but left the invoice wrong is worse than a visible error.
 */
export async function recalculateInvoiceStatus(invoiceId: string): Promise<void> {
  const [invoice, rows] = await Promise.all([
    getInvoice(invoiceId),
    listTransactions({ invoice_id: invoiceId }),
  ])
  const amountPaid = rows
    .filter(t => INVOICE_PAYMENT_TYPES.has(String(t.type)))
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  const balanceDue = Math.max(0, Number(invoice.total_amount) - amountPaid)

  let status = invoice.status
  if (invoice.status !== 'cancelled') {
    if (amountPaid > 0 && balanceDue <= 0) status = 'paid'
    else if (amountPaid > 0) status = 'partial'
    else if (invoice.status === 'paid' || invoice.status === 'partial') status = 'sent'
  }

  const paidDate = status === 'paid'
    ? (invoice.paid_date || localDateString())
    : null

  if (
    status !== invoice.status ||
    invoice.amount_paid !== amountPaid ||
    invoice.balance_due !== balanceDue ||
    (invoice.paid_date ?? null) !== paidDate
  ) {
    await updateInvoice(invoiceId, { status, amount_paid: amountPaid, balance_due: balanceDue, paid_date: paidDate })
  }
}

export async function createTransaction(payload: TransactionPayload, createdBy: string) {
  try {
    const record = await pb.collection('transactions').create({
      ...payload,
      created_by: createdBy,
    })

    if (payload.invoice_id) {
      await recalculateInvoiceStatus(payload.invoice_id)
    }

    return mapTransaction(record)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      console.warn('Transactions collection not found or inaccessible', error)
      throw new Error('Failed to create transaction. Please ensure the transactions collection exists in PocketBase.')
    }
    throw error
  }
}

export async function updateTransaction(
  transactionId: string,
  payload: Partial<TransactionPayload>,
) {
  try {
    // The invoice this row used to belong to needs recalculating too, in
    // case the edit moved or unlinked the payment.
    const before = await pb.collection('transactions').getOne<Record<string, any>>(transactionId)
    const record = await pb.collection('transactions').update(transactionId, payload)
    const touched = new Set<string>([before.invoice_id, record.invoice_id].filter(Boolean))
    for (const invoiceId of touched) await recalculateInvoiceStatus(invoiceId)
    return mapTransaction(record)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      console.warn('Transaction not found or inaccessible', error)
      throw new Error('Failed to update transaction')
    }
    throw error
  }
}

export async function deleteTransaction(transactionId: string) {
  try {
    const before = await pb.collection('transactions').getOne<Record<string, any>>(transactionId)
    await pb.collection('transactions').delete(transactionId)
    if (before.invoice_id) await recalculateInvoiceStatus(before.invoice_id)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      console.warn('Transaction not found or inaccessible', error)
      throw new Error('Failed to delete transaction')
    }
    throw error
  }
}

export async function listInvoiceTransactions(invoiceId: string) {
  return listTransactions({ invoice_id: invoiceId })
}

export async function listClientTransactions(clientId: string) {
  return listTransactions({ client_id: clientId })
}

// Helper functions
export function getTotalRevenue(transactions: Transaction[]): number {
  return transactions.reduce((sum, t) => sum + t.amount, 0)
}
